<template>
    <div class="mx-2 mx-md-0">
        <!-- Modals for History -->
        <b-modal id="modal-received-history" scrollable :title="`RECEIVED HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveReceivedHistory">
                        <b-form-group label="Add Received Quantity">
                            <b-input type="number" v-model="newReceivedQty" placeholder="Enter quantity" required></b-input>
                        </b-form-group>
                        <b-button type="submit" variant="success" size="sm">Add Received</b-button>
                    </b-form>
                </b-card>

              <b-table striped hover :items="receivedHistory" :fields="historyFields" class="mt-3">
                <template #cell(timestamp)="data">
                    {{ formatDate(data.value) }}
                </template>
                <template #cell(actions)="data">
                    <b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('received', data.item.id)">
                        <b-icon icon="trash"></b-icon>
                    </b-button>
                </template>
            </b-table>
            </div>
        </b-modal>

        <!-- Add to Damages Modal -->
        <b-modal 
            id="modal-add-to-damages" 
            scrollable 
            title="ADD ITEM TO AS IS" 
            size="lg" 
            @hidden="reset_damages_form"
            @close="handleModalClose"
            @hide="handleModalClose"
            no-close-on-backdrop
            no-close-on-esc
        >
            <div v-if="selectedItem">
                <b-card class="mb-3">
                    <b-row>
                        <b-col>
                            <strong>Product:</strong> {{ selectedItem.product_name }}
                        </b-col>
                        <b-col>
                            <strong>SKU:</strong> {{ selectedItem.product_sku }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <strong>Current Location:</strong> {{ packet.locsubsdict[selectedItem.locsub] }}
                        </b-col>
                        <b-col>
                            <strong>Current Stock:</strong> {{ selectedItem.in_stock }}
                        </b-col>
                    </b-row>
                </b-card>

                <b-form @submit.prevent="save_damaged_item">
                    <!-- Source Location (Where to take FROM) -->
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Source Location" label-for="source-location">
                                <b-form-select
                                    id="source-location"
                                    v-model="newDamageItem.source_locsub"
                                    :options="availableLocations"
                                    required
                                    @change="onSourceLocationChange"
                                    :disabled="isSavingDamages"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null">Select Source Location</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                         <b-col md="6">
                            <b-form-group label="Target Location" label-for="target-location">
                                <b-form-select
                                    id="target-location"
                                    v-model="newDamageItem.target_locsub"
                                    :options="availableLocations"
                                    required
                                    @change="onTargetLocationChange"
                                    :disabled="isSavingDamages"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null">Select Target Location</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Rest of the form remains the same -->
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="AS IS Type" label-for="damage-type">
                                <b-form-select
                                    id="damage-type"
                                    v-model="newDamageItem.damage_type"
                                    :options="damageTypeOptions"
                                    required
                                    :disabled="isSavingDamages"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="AS IS Grade" label-for="damage-grade">
                                <b-form-select
                                    id="damage-grade"
                                    v-model="newDamageItem.damage_grade"
                                    :options="damageGradeOptions"
                                    required
                                    :disabled="isSavingDamages"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Pricing -->
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Original Price" label-for="original-price">
                                <b-form-input
                                    id="original-price"
                                    type="number"
                                    step="0.01"
                                    v-model="newDamageItem.original_price"
                                    placeholder="Enter original price"
                                    :disabled="isSavingDamages"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Selling Price" label-for="selling-price">
                                <b-form-input
                                    id="selling-price"
                                    type="number"
                                    step="0.01"
                                    v-model="newDamageItem.selling_price"
                                    placeholder="Enter selling price for AS IS item"
                                    required
                                    :disabled="isSavingDamages"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Quantity and Description -->
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Quantity to Mark as Damaged" label-for="damage-quantity">
                                <b-form-input
                                    id="damage-quantity"
                                    type="number"
                                    v-model="newDamageItem.quantity"
                                    :max="getMaxAvailableQuantity()"
                                    min="1"
                                    required
                                    :disabled="isSavingDamages"
                                ></b-form-input>
                                <small class="text-muted">Max available from source: {{ getMaxAvailableQuantity() }}</small>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Damage Description -->
                    <b-form-group label="AS IS Description" label-for="damage-description">
                        <b-form-textarea
                            id="damage-description"
                            v-model="newDamageItem.damage_description"
                            placeholder="Describe the damage in detail..."
                            rows="3"
                            :disabled="isSavingDamages"
                        ></b-form-textarea>
                    </b-form-group>

                    <!-- Image Upload -->
                    <b-form-group label="AS IS Images" label-for="damage-images">
                        <b-form-file
                            id="damage-images"
                            v-model="damageImages"
                            multiple
                            accept="image/*"
                            placeholder="Choose images or drop them here..."
                            drop-placeholder="Drop images here..."
                            @change="handleImageUpload"
                            :disabled="isSavingDamages"
                        ></b-form-file>
                        <small class="text-muted">You can upload multiple images to show the damage</small>
                        
                        <!-- Image Preview -->
                        <div v-if="imagePreviews.length > 0" class="mt-3">
                            <h6>Image Previews:</h6>
                            <div class="d-flex flex-wrap">
                                <div v-for="(preview, index) in imagePreviews" :key="index" class="mr-2 mb-2">
                                    <img :src="preview" alt="Damage preview" style="max-width: 100px; max-height: 100px; object-fit: cover;">
                                    <b-button 
                                        size="sm" 
                                        variant="outline-danger" 
                                        @click="removeImage(index)"
                                        class="mt-1"
                                        style="width: 100%;"
                                        :disabled="isSavingDamages"
                                    >
                                        Remove
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </b-form-group>
                </b-form>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="reset_damages_form" :disabled="isSavingDamages">Cancel</b-button>
                <b-button variant="danger" @click="save_damaged_item" :disabled="isSavingDamages">
                    <b-spinner v-if="isSavingDamages" small class="mr-2"></b-spinner>
                    <span v-if="isSavingDamages">Processing...</span>
                    <span v-else>Add to AS IS</span>
                </b-button>
            </template>
        </b-modal>

        <b-modal id="modal-missing-history" scrollable :title="`MISSING HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveMissingHistory">
                        <b-form-group label="Add Missing Quantity">
                            <b-input type="number" v-model="newMissingQty" placeholder="Enter quantity" required></b-input>
                        </b-form-group>
                        <b-button type="submit" variant="warning" size="sm">Add Missing</b-button>
                    </b-form>
                </b-card>

                <b-table striped hover :items="missingHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">
                        {{ formatDate(data.value) }}
                    </template>
                    <template #cell(actions)="data">
                        <b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('missing', data.item.id)">
                            <b-icon icon="trash"></b-icon>
                        </b-button>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Fixed Damages History Modal -->
        <b-modal id="modal-damages-history" 
                 scrollable 
                 :title="`AS IS HISTORY - ${currentDamagesItem ? currentDamagesItem.product_name : ''}`" 
                 size="xl" 
                 hide-footer
                 @hidden="handleDamagesHistoryModalClose">
            <div v-if="currentDamagesItem">
                <b-card class="mb-3">
                    <b-row>
                        <b-col md="6">
                            <strong>Product:</strong> {{ currentDamagesItem.product_name }}
                        </b-col>
                        <b-col md="6">
                            <strong>SKU:</strong> {{ currentDamagesItem.product_sku }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <strong>Total AS IS Items:</strong> {{ damagesHistory.length }}
                        </b-col>
                        <b-col md="6">
                            <strong>Available for Sale:</strong> 
                            {{ damagesHistory.filter(d => d.status === 'available').length }}
                        </b-col>
                    </b-row>
                </b-card>

                <b-table striped hover :items="damagesHistory" :fields="damagesHistoryFields" class="mt-3" small>
                    <template #cell(moved_date)="data">
                        {{ formatDate(data.value) }}
                    </template>
                    <template #cell(source_location_display)="data">
                        {{ data.value }}
                    </template>
                    <template #cell(target_location_display)="data">
                        {{ data.value }}
                    </template>
                    <template #cell(images)="data">
                        <div v-if="data.value && data.value.length > 0">
                            <div class="damage-images-thumbnails">
                                <img 
                                    v-for="(image, imgIndex) in data.value" 
                                    :key="imgIndex"
                                    :src="image.image_url" 
                                    :alt="image.image_alt_text"
                                    class="damage-thumbnail"
                                    @click="showDamageImageGallery(data.item, imgIndex)"
                                    title="Click to view image"
                                />
                            </div>
                        </div>
                        <div v-else>
                            <b-badge variant="secondary">No images</b-badge>
                        </div>
                    </template>
                </b-table>

                <div v-if="damagesHistory.length === 0" class="text-center text-muted p-4">
                    <b-icon icon="exclamation-octagon" font-scale="2"></b-icon>
                    <p class="mt-2">No damages history found for this item</p>
                </div>
            </div>
        </b-modal>

        <b-modal id="modal-damage-images-gallery" 
                 title="Damage Images" 
                 size="xl" 
                 hide-footer
                 @hidden="closeImageGallery">
            <div v-if="currentImageGallery.damage">
                <b-card class="mb-3">
                    <b-row>
                        <b-col md="6">
                            <strong>Product:</strong> {{ currentImageGallery.damage.product_name }}
                        </b-col>
                        <b-col md="6">
                            <strong>SKU:</strong> {{ currentImageGallery.damage.product_sku }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <strong>Damage Type:</strong> 
                            <b-badge :variant="getDamageTypeVariant(currentImageGallery.damage.damage_type)">
                                {{ currentImageGallery.damage.damage_type }}
                            </b-badge>
                        </b-col>
                        <b-col md="6">
                            <strong>Damage Grade:</strong> 
                            <b-badge :variant="getDamageGradeVariant(currentImageGallery.damage.damage_grade)">
                                {{ currentImageGallery.damage.damage_grade }}
                            </b-badge>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Image Gallery -->
                <div v-if="currentImageGallery.images && currentImageGallery.images.length > 0">
                    <!-- Main Image Display -->
                    <div class="text-center mb-3 main-image-container">
                        <img 
                            :src="currentImageGallery.images[currentImageGallery.currentIndex].image_url" 
                            :alt="currentImageGallery.images[currentImageGallery.currentIndex].image_alt_text"
                            class="main-damage-image"
                        />
                    </div>

                    <!-- Image Navigation -->
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <b-button 
                            variant="outline-primary" 
                            @click="prevImage"
                            :disabled="currentImageGallery.currentIndex === 0"
                        >
                            <b-icon icon="chevron-left"></b-icon> Previous
                        </b-button>
                        
                        <span class="image-counter">
                            Image {{ currentImageGallery.currentIndex + 1 }} of {{ currentImageGallery.images.length }}
                        </span>
                        
                        <b-button 
                            variant="outline-primary" 
                            @click="nextImage"
                            :disabled="currentImageGallery.currentIndex === currentImageGallery.images.length - 1"
                        >
                            Next <b-icon icon="chevron-right"></b-icon>
                        </b-button>
                    </div>

                    <!-- Thumbnail Strip -->
                    <div class="thumbnail-strip">
                        <div 
                            v-for="(image, index) in currentImageGallery.images" 
                            :key="index"
                            class="thumbnail-container"
                            :class="{ 'active': index === currentImageGallery.currentIndex }"
                            @click="goToImage(index)"
                        >
                            <img 
                                :src="image.image_url" 
                                :alt="image.image_alt_text"
                                class="thumbnail-image"
                            />
                        </div>
                    </div>
                </div>

                <div v-else class="text-center text-muted p-4">
                    <b-icon icon="image" font-scale="2"></b-icon>
                    <p class="mt-2">No images available</p>
                </div>
            </div>
        </b-modal>

        <!-- Damage Details Modal -->
        <b-modal id="modal-damage-details" :title="`Damage Details - ${selectedDamage ? selectedDamage.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedDamage">
                <b-card class="mb-3">
                    <b-row>
                        <b-col md="6">
                            <strong>Product SKU:</strong> {{ selectedDamage.product_sku }}
                        </b-col>
                        <b-col md="6">
                            <strong>Status:</strong> 
                            <b-badge :variant="selectedDamage.status === 'available' ? 'success' : 'secondary'">
                                {{ selectedDamage.status }}
                            </b-badge>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <strong>Moved From:</strong> {{ selectedDamage.source_location }} - {{ selectedDamage.source_sublocation }}
                        </b-col>
                        <b-col md="6">
                            <strong>Moved To:</strong> {{ selectedDamage.target_location }} - {{ selectedDamage.target_sublocation }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <strong>Damage Type:</strong> 
                            <b-badge :variant="getDamageTypeVariant(selectedDamage.damage_type)">
                                {{ selectedDamage.damage_type }}
                            </b-badge>
                        </b-col>
                        <b-col md="6">
                            <strong>Damage Grade:</strong> 
                            <b-badge :variant="getDamageGradeVariant(selectedDamage.damage_grade)">
                                {{ selectedDamage.damage_grade }}
                            </b-badge>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <strong>Original Price:</strong> ${{ (selectedDamage.original_price || 0).toFixed(2) }}
                        </b-col>
                        <b-col md="6">
                            <strong>Selling Price:</strong> ${{ (selectedDamage.selling_price || 0).toFixed(2) }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2" v-if="selectedDamage.damage_description">
                        <b-col>
                            <strong>Damage Description:</strong>
                            <p class="mt-1">{{ selectedDamage.damage_description }}</p>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Damage Images -->
                <b-card title="Damage Images" v-if="selectedDamage.images && selectedDamage.images.length > 0">
                    <b-row>
                        <b-col md="4" v-for="(image, index) in selectedDamage.images" :key="index" class="mb-3">
                            <b-card no-body>
                                <b-card-body class="text-center p-2">
                                    <img 
                                        :src="image.image_url" 
                                        :alt="image.image_alt_text"
                                        style="max-width: 100%; max-height: 150px; object-fit: cover; cursor: pointer;"
                                        @click="showImageModal(image.image_url)"
                                    >
                                </b-card-body>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card v-else title="Damage Images">
                    <div class="text-center text-muted p-3">
                        <b-icon icon="image" font-scale="2"></b-icon>
                        <p class="mt-2">No images available for this damage</p>
                    </div>
                </b-card>
            </div>
        </b-modal>

        <!-- Image Preview Modal -->
        <b-modal id="modal-image-preview" title="Damage Image" size="lg" hide-footer>
            <div class="text-center">
                <img :src="selectedImage" alt="Damage image" style="max-width: 100%; max-height: 70vh;">
            </div>
        </b-modal>

        <b-modal id="modal-sold-history" scrollable :title="`SOLD HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveSoldHistory">
                        <b-form-group label="Add Sold Quantity">
                            <b-input type="number" v-model="newSoldQty" placeholder="Enter quantity" required></b-input>
                        </b-form-group>
                        <b-button type="submit" variant="info" size="sm">Add Sold</b-button>
                    </b-form>
                </b-card>

                <b-table striped hover :items="soldHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">
                        {{ formatDate(data.value) }}
                    </template>
                    <template #cell(actions)="data">
                        <b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('sold', data.item.id)">
                            <b-icon icon="trash"></b-icon>
                        </b-button>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Edit Modals -->
        <b-modal id="modal-edit-received" title="Edit Received Quantity" @ok="saveEditedReceived">
            <b-form-group label="Received Quantity">
                <b-input type="number" v-model="editReceivedQty" required></b-input>
            </b-form-group>
        </b-modal>

        <b-modal id="modal-edit-missing" title="Edit Missing Quantity" @ok="saveEditedMissing">
            <b-form-group label="Missing Quantity">
                <b-input type="number" v-model="editMissingQty" required></b-input>
            </b-form-group>
        </b-modal>

        <b-modal id="modal-edit-damages" title="Edit Damages Quantity" @ok="saveEditedDamages">
            <b-form-group label="Damages Quantity">
                <b-input type="number" v-model="editDamagesQty" required></b-input>
            </b-form-group>
        </b-modal>

        <b-modal id="modal-edit-sold" title="Edit Sold Quantity" @ok="saveEditedSold">
            <b-form-group label="Sold Quantity">
                <b-input type="number" v-model="editSoldQty" required></b-input>
            </b-form-group>
        </b-modal>

        <b-modal id="modal-reserved-invoices" scrollable :title="`RESERVED INVOICES - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card class="mb-3">
                    <b-row>
                        <b-col>
                            <strong>SKU:</strong> {{ selectedItem.product_sku }}
                        </b-col>
                        <b-col>
                            <strong>Location:</strong> {{ packet.locsubsdict[selectedItem.locsub] }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <strong>Total Reserved:</strong> {{ selectedItem.total_reserved }}
                        </b-col>
                        <b-col>
                            <strong>Total Invoices:</strong> {{ selectedItem.total_invoices }}
                        </b-col>
                    </b-row>
                </b-card>

                <b-table striped hover :items="reservedInvoices" :fields="reservedInvoiceFields" class="mt-3">
                    <template #cell(invoice_id)="data">
                        <a :href="`http://localhost:8081/#/sales/viewsale/${data.value}/`"
                           target="_blank"
                           class="invoice-link">
                            {{ data.value }}
                            <b-icon icon="box-arrow-up-right" scale="0.8"></b-icon>
                        </a>
                    </template>
                    <template #cell(invoice_date)="data">
                        {{ formatDate(data.value) }}
                    </template>
                    <template #cell(reserved_quantity)="data">
                        {{ data.value }}
                    </template>
                    <template #cell(invoice_completed)="data">
                        <b-badge :variant="data.value ? 'success' : 'warning'">
                            {{ data.value ? 'Completed' : 'Pending' }}
                        </b-badge>
                    </template>
                </b-table>

                <div v-if="reservedInvoices.length === 0" class="text-center text-muted p-4">
                    <b-icon icon="file-earmark-x" font-scale="2"></b-icon>
                    <p class="mt-2">No reserved invoices found for this item</p>
                </div>
            </div>
        </b-modal>

        <!-- Main Content -->
        <div
          v-if="isMobile"
          class="d-flex justify-content-between align-items-center mb-2 py-2"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            @click="filtersVisible = !filtersVisible"
          >
            <b-icon-funnel-fill class="mr-1" />
            {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
          </b-button>
        </div>

        <div class="d-flex flex-column flex-lg-row" style="gap: 7px;">
          <b-card style="display: inline-block; width: 100%; vertical-align: top;" :style="{'max-width': isMobile ? 'none' : '300px'}">
              <div>LOCATION</div>
              <div>
                  <b-form-select
                      :options="locations"
                      v-model="location_id"
                      @change="get_inventory_for_location"
                      style="width: 100%;"
                      :style="{'max-width': isMobile ? 'none' : '250px'}"
                  >
                      <template #first>
                          <b-form-select-option :value="null" :disabled="true">PICK A LOCATION</b-form-select-option>
                      </template>
                  </b-form-select>
              </div>
          </b-card>

          <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
              <b-card style="display: inline-block; width: 100%; vertical-align: top;" v-if="items.length > 0" :style="{'max-width': isMobile ? 'none' : '300px'}">
                  <div>FILTER SUBLOCATION</div>
                  <div>
                      <b-form-select
                          :options="packet.locsubs.filter(d => d.location_id == location_id)"
                          v-model="picked_locsub"
                          style="width: 100%;"
                          :style="{'max-width': isMobile ? 'none' : '250px'}"
                      >
                          <template #first>
                              <b-form-select-option :value="null">ALL</b-form-select-option>
                          </template>
                      </b-form-select>
                  </div>
              </b-card>

              <b-card style="display: inline-block; width: 100%; vertical-align: top;" v-if="items.length > 0" :style="{'max-width': isMobile ? 'none' : '300px'}">
                  <div>FILTER CATEGORY</div>
                  <div>
                      <b-form-select
                          :options="categoryOptions"
                          v-model="picked_category"
                          style="width: 100%;"
                          :style="{'max-width': isMobile ? 'none' : '250px'}"
                      >
                          <template #first>
                              <b-form-select-option :value="null">ALL</b-form-select-option>
                          </template>
                      </b-form-select>
                  </div>
              </b-card>

              <b-card style="display: inline-block; max-width: auto; vertical-align: top;" v-if="items.length > 0">
                  <div>
                      <div style="margin: 3px; display: inline-block; vertical-align: top;">
                          <div v-if="!hide_zero_in_stock">
                              <b-button variant="success" size="sm" @click="hide_zero_in_stock = !hide_zero_in_stock">
                                  HIDE 0 IN-STOCK
                              </b-button>
                          </div>
                          <div v-else>
                              <b-button variant="warning" size="sm" @click="hide_zero_in_stock = !hide_zero_in_stock">
                                  SHOW 0 IN-STOCK
                              </b-button>
                          </div>
                      </div>

                      <div style="margin: 3px; display: inline-block; vertical-align: top;">
                          <b-button variant="success" size="sm" @click="get_inventory_for_location">REFRESH</b-button>
                      </div>

                      <div style="margin: 3px; display: inline-block; vertical-align: top;" v-if="packet.userDetails.userrole == 'admin'">
                          <b-button variant="info" size="sm" @click="downloadExcel">DOWNLOAD EXCEL</b-button>
                      </div>
                  </div>

                  <div style="margin-top: 5px;">
                      <b-form-input
                          v-model="search_txt"
                          size="sm"
                          placeholder="SEARCH PRODUCT IN RESULTS"
                      ></b-form-input>
                  </div>
              </b-card>

          </b-collapse>
        </div>

        <b-card style="margin-top: 10px; min-height: 250px; max-height: 100%; overflow-y: scroll;">
            <div v-if="items_being_loaded">
                <loading
                    :active="true"
                    :can-cancel="false"
                    :is-full-page="false">
                </loading>
            </div>

            <div v-else>
                <div v-if="items.length == 0">
                    <b-alert variant="warning" show>NO INVENTORY FOR THIS LOCATION</b-alert>
                </div>

                <div v-else style="overflow-x: auto;">
                    <table style="width: 100%;">
                        <thead>
                            <tr>
                                <th class="ipt_tdhr" @click="sortby('product_name')" style="min-width: 150px;">
                                    PRODUCT NAME
                                </th>
                                <th class="ipt_tdhr" @click="sortby('product_sku')" style="min-width: 80px;">
                                    PRODUCT SKU
                                </th>
                                <th class="ipt_tdhr" @click="sortby('category')" style="min-width: 70px;">
                                    CATEGORY
                                </th>
                                <th class="ipt_tdhr" @click="sortby('locsub')"  style="min-width: 70px;">
                                    SUBLOCATION
                                </th>
                                <th class="ipt_tdhr" @click="sortby('in_stock')"  style="min-width: 70px;">
                                    IN STOCK
                                </th>
                                <th class="ipt_tdhr" @click="sortby('reserved')"  style="min-width: 70px;">
                                    RESERVED
                                </th>
                                <th class="ipt_tdhr" @click="sortby('available')"  style="min-width: 70px;">
                                    AVAILABLE
                                </th>
                                <th class="ipt_tdhr" @click="sortby('damages')"  style="min-width: 70px;">
                                    DAMAGES
                                </th>
                                <th class="ipt_tdhr"  style="min-width: 70px;">
                                    ACTIONS
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr>
                                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;"></td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                                    {{ total('in_stock') }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                                    {{ total('reserved') }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                                    {{ total('available') }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                                    {{ total('damages') }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;"></td>
                            </tr>
                        </tbody>

                        <tbody>
                            <tr v-for="item,idx in items" v-bind:key="idx" v-if="allowed(item)">
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    {{ item.product_name }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    {{ item.product_sku }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    {{ item.category }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    {{ packet.locsubsdict[item.locsub] }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    <span v-if="item.edit_in_stock && !item.is_out_of_stock">
                                        <span><b-input size="sm" v-model="item.in_stock_fake" style="width: 80px;" :disabled="item.is_out_of_stock"></b-input></span>
                                        <span><b-badge href="#" variant="success" @click="save_in_stock(item)" :disabled="item.is_out_of_stock">SAVE</b-badge></span>
                                        <span><b-badge href="#" variant="secondary" @click="cancel_save_in_stock(item)">CANCEL</b-badge></span>
                                    </span>

                                    <span v-else>
                                        <span v-if="(packet.userDetails.userrole == 'admin' || packet.userDetails.userrole == 'officemanager') && !item.is_out_of_stock">
                                            <b-icon icon="pencil-square" scale="0" variant="success" @click="edit_in_stock(item)"></b-icon>
                                        </span>
                                        <span>{{ (item.in_stock || 0) }}</span>
                                    </span>
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    <span class="clickable-cell"
                                          @click="show_reserved_invoices(item)"
                                          title="Click to view reserved invoices"
                                          :class="{'': item.reserved > 0}">
                                        <b-icon icon="clipboard-check" style="color: orange;" scale="1"  v-if="item.reserved > 0"></b-icon>
                                        {{ (item.reserved || 0) }}
                                    </span>
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    {{ (item.available || 0) }}
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal;">
                                    <span class="clickable-cell" @click.stop="loadAndShowDamages(item)" title="Click to view damages history">
                                        <template v-if="item.damages_count_loaded">
                                            {{ item.actual_damages_count || 0 }}
                                            <b-icon icon="exclamation-octagon" scale="0.8" variant="danger"></b-icon>
                                        </template>
                                        <template v-else>
                                            <span style="color: #6c757d; font-size: 0.9em;">
                                                Click to load
                                                <b-icon icon="exclamation-octagon" scale="0.8" variant="secondary"></b-icon>
                                            </span>
                                        </template>
                                    </span>
                                </td>
                                <td class="ipt_tdhr" style="font-weight: normal; padding: 3px; max-width: 350px;">
                                    <button @click="show_add_to_damages(item)" v-if="packet.userDetails.userrole == 'admin' || packet.userDetails.userrole == 'officemanager'" style="margin-left: 5px; background-color: #dc3545; color: white; border: none; padding: 4px 8px; border-radius: 3px;">
                                        ADD TO AS IS
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
    import moment from 'moment'
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import Requestor from '@/utilities/Requestor';
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';
    import { BIconFunnelFill } from "bootstrap-vue";

    import ItemComments from '@/components/importables/ItemComments'

    export default {
        name: "InventoryByLocation",
        components: {flatPickr, Loading, ItemComments},
        data: function(){
            return {
                items: [],
                newDamageItem: {
                    source_locsub: null,
                    target_locsub: null,
                    damage_type: '',
                    damage_grade: 'moderate',
                    damage_description: '',
                    original_price: null,
                    selling_price: null,
                    quantity: 1
                },
                isSavingDamages: false, // Add this flag to prevent multiple submissions
                currentImageGallery: {
                    damage: null,
                    images: [],
                    currentIndex: 0
                },
                damagesHistoryFields: [
                    { key: 'moved_date', label: 'DATE MOVED', sortable: true },
                    { key: 'source_location_display', label: 'MOVED FROM', sortable: true },
                    { key: 'target_location_display', label: 'MOVED TO', sortable: true },
                    { key: 'moved_by', label: 'MOVED BY', sortable: true },
                    { key: 'images', label: 'IMAGES', sortable: false },
                ],
                selectedDamage: null,
                selectedImage: null,
                damageImages: [],
                availableLocations: [],   
                imagePreviews: [],
                damageTypeOptions: [
                    { value: 'customer_return', text: 'Customer Return' },
                    { value: 'shipping', text: 'Shipping Damage' },
                    { value: 'warehouse_handling', text: 'Warehouse Handling' },
                    { value: 'vendor_fault', text: 'Vendor Fault' },
                    { value: 'quality_control', text: 'Quality Control' },
                    { value: 'other', text: 'Other' }
                ],
                damageGradeOptions: [
                    { value: 'light', text: 'Light Damage' },
                    { value: 'moderate', text: 'Moderate Damage' },
                    { value: 'heavy', text: 'Heavy Damage' }
                ],
                locations: [],
                location_id:null,
                packet: {},
                items_being_loaded: false,
                item_comments_invoice_id: null,
                item_comments_itemid: null,
                picked_locsub: null,
                sort_asc: false,
                hide_zero_in_stock: false,
                search_txt: "",
                picked_category: null,
                categoryOptions: [],
                reservedInvoices: [],
                reservedInvoiceFields: [
                    { key: 'invoice_id', label: 'INVOICE ID', sortable: true },
                    { key: 'reserved_quantity', label: 'RESERVED QTY', sortable: true },
                    { key: 'invoice_date', label: 'INVOICE DATE', sortable: true },
                    { key: 'invoice_completed', label: 'STATUS', sortable: true }
                ],
                // New data properties for history modals
                selectedItem: null,
                currentDamagesItem: null, // Separate item for damages history
                receivedHistory: [],
                missingHistory: [],
                damagesHistory: [],
                soldHistory: [],
                newReceivedQty: 0,
                newMissingQty: 0,
                newDamagesQty: 0,
                newSoldQty: 0,
                editReceivedQty: 0,
                editMissingQty: 0,
                editDamagesQty: 0,
                editSoldQty: 0,
                historyFields: [
                    { key: 'timestamp', label: 'DATE', sortable: true },
                    { key: 'changed_by', label: 'BY', sortable: true },
                    { key: 'quantity', label: 'QTY', sortable: true },
                    { key: 'sublocation_name', label: 'LOC', sortable: true },
                    { key: 'source', label: 'SOURCE', sortable: true },
                    { key: 'actions', label: 'ACTION' }
                ],
                filtersVisible: false,
                isMobile: false,
                damageHistoryDebounce: false,
                loadingDamagesHistory: false,
            }
        },
        watch: {
            items(newItems) {
                let categories = [...new Set(newItems.map(i => i.category).filter(Boolean))];
                this.categoryOptions = categories.map(c => ({
                    value: c,
                    text: c
                }));
            }
        },
        computed: {
            availableLocations() {
                const uniqueLocations = [];
                const seenLocations = new Set();
                
                this.packet.locsubs.forEach(loc => {
                    let locParts = loc.value.split("|");
                    let locationId = locParts[0];
                    let locationName = loc.text.split(' - ')[0];
                    
                    if (!seenLocations.has(locationId)) {
                        seenLocations.add(locationId);
                        uniqueLocations.push({
                            value: locationId,
                            text: locationName
                        });
                    }
                });
                
                return uniqueLocations;
            },

            orderedTotal() {
                return this.items
                .filter(item => this.allowed(item))
                .reduce((sum, item) => sum + Number(item.ordered || 0), 0);
            },
            scheduledTotal() {
                return this.items
                .filter(item => this.allowed(item))
                .reduce((sum, item) => sum + Number(item.scheduled || 0), 0);
            },
            soldsofarTotal() {
                return this.items
                .filter(item => this.allowed(item))
                .reduce((sum, item) => sum + Number(item.sold_so_far || 0), 0);
            }
        },
        mounted() {
            this.handleResize();
            window.addEventListener("resize", this.handleResize);
            
            // Add modal event listeners
            this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
                if (modalId === 'modal-damages-history') {
                    this.handleDamagesHistoryModalClose();
                }
            });
        },
        beforeDestroy() {
            window.removeEventListener("resize", this.handleResize);
        },
        methods: {
            handleModalClose(bvModalEvt) {
                if (this.isSavingDamages) {
                    bvModalEvt.preventDefault();
                    this.$bvToast.toast(
                        "Please wait, AS IS item is being saved...",
                        {title: "Processing", autoHideDelay: 1500, variant:"info"}
                    );
                }
            },
            loadAndShowDamages: function(item) {
                if (!item.damages_count_loaded) {
                    this.loadItemDamagesCount(item, () => {
                        this.show_damages_history(item);
                    });
                } else {
                    this.show_damages_history(item);
                }
            },

            loadItemDamagesCount: function(item, callback) {
                let path = "/api/inventory/get_damages_count/";
                let that = this;
                
                let splitted = item.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);
                
                let data = {
                    product_sku: item.product_sku,
                    location_id: location_id,
                    sublocation_id: sublocation_id
                };
                
                // Show loading state
                let targetItem = that.items.find(i => 
                    i.product_sku === item.product_sku && 
                    i.locsub === item.locsub
                );
                if (targetItem) {
                    that.$set(targetItem, 'actual_damages_count', 'Loading...');
                }
                
                let s = function(response) {
                    if (response.data.result !== undefined && !response.data.error) {
                        // Update the item with actual damages count
                        let targetItem = that.items.find(i => 
                            i.product_sku === item.product_sku && 
                            i.locsub === item.locsub
                        );
                        if (targetItem) {
                            that.$set(targetItem, 'actual_damages_count', response.data.result);
                            that.$set(targetItem, 'damages', response.data.result);
                            that.$set(targetItem, 'damages_count_loaded', true);
                        }
                    } else {
                        // Set to 0 on error or no data
                        let targetItem = that.items.find(i => 
                            i.product_sku === item.product_sku && 
                            i.locsub === item.locsub
                        );
                        if (targetItem) {
                            that.$set(targetItem, 'actual_damages_count', 0);
                            that.$set(targetItem, 'damages', 0);
                            that.$set(targetItem, 'damages_count_loaded', true);
                        }
                    }
                    
                    // Execute callback
                    if (callback) callback();
                }
                
                let f = function(error) {
                    console.error('Error fetching damages count for:', item.product_sku, error);
                    let targetItem = that.items.find(i => 
                        i.product_sku === item.product_sku && 
                        i.locsub === item.locsub
                    );
                    if (targetItem) {
                        that.$set(targetItem, 'actual_damages_count', 0);
                        that.$set(targetItem, 'damages', 0);
                        that.$set(targetItem, 'damages_count_loaded', true);
                    }
                    
                    // Execute callback even on error
                    if (callback) callback();
                }
                
                Requestor.getSync(path, data, s, f);
            },
            showDamageImageGallery: function(damage, startIndex = 0) {
                this.currentImageGallery = {
                    damage: damage,
                    images: damage.images || [],
                    currentIndex: startIndex
                };
                this.$bvModal.show('modal-damage-images-gallery');
            },

            // Navigate to previous image
            prevImage: function() {
                if (this.currentImageGallery.currentIndex > 0) {
                    this.currentImageGallery.currentIndex--;
                }
            },

            // Navigate to next image
            nextImage: function() {
                if (this.currentImageGallery.currentIndex < this.currentImageGallery.images.length - 1) {
                    this.currentImageGallery.currentIndex++;
                }
            },

            // Go to specific image
            goToImage: function(index) {
                this.currentImageGallery.currentIndex = index;
            },

            // Close gallery and reset
            closeImageGallery: function() {
                this.currentImageGallery = {
                    damage: null,
                    images: [],
                    currentIndex: 0
                };
            },

            // Alternative: Show single image in modal (simpler approach)
            showDamageImage: function(imageUrl, altText) {
                this.selectedImage = imageUrl;
                this.$bvModal.show('modal-image-preview');
            },
            getDamageTypeVariant: function(damageType) {
                const variants = {
                    'Customer Return': 'primary',
                    'Shipping Damage': 'warning',
                    'Warehouse Handling': 'info',
                    'Vendor Fault': 'danger',
                    'Quality Control': 'secondary',
                    'Other': 'dark'
                };
                return variants[damageType] || 'secondary';
            },

            getDamageGradeVariant: function(damageGrade) {
                const variants = {
                    'Light Damage': 'success',
                    'Moderate Damage': 'warning',
                    'Heavy Damage': 'danger'
                };
                return variants[damageGrade] || 'secondary';
            },

            showDamageDetails: function(damage) {
                this.selectedDamage = damage;
                this.$bvModal.show('modal-damage-details');
            },

            showImageModal: function(imageUrl) {
                this.selectedImage = imageUrl;
                this.$bvModal.show('modal-image-preview');
            },

            // Fixed show_damages_history method
            show_damages_history: function(item) {
                // Check if already processing
                if (this.damageHistoryDebounce) {
                    return;
                }
                
                this.damageHistoryDebounce = true;
                
                console.log('Opening damages history for:', item.product_name, 'SKU:', item.product_sku);
                
                // Clear previous data first
                this.damagesHistory = [];
                
                // Set new selected item for damages history
                this.currentDamagesItem = {...item};
                
                // Show the fixed modal ID
                this.$bvModal.show('modal-damages-history');
                
                // Fetch data
                this.fetchDamagesHistory();
                
                // Reset debounce
                setTimeout(() => {
                    this.damageHistoryDebounce = false;
                }, 1000);
            },

            handleDamagesHistoryModalClose: function() {
                // Reset state when modal closes
                this.currentDamagesItem = null;
                this.damagesHistory = [];
                this.damageHistoryDebounce = false;
            },

            fetchDamagesHistory: function() {
                const currentItem = this.currentDamagesItem;
                if (!currentItem) {
                    console.error('No selected item available');
                    this.damagesHistory = [];
                    return;
                }

                console.log('Fetching damages history for item:', currentItem);

                let splitted = currentItem.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);

                let data = {
                    product_sku: currentItem.product_sku,
                    product_id: currentItem.product_id || null,
                    location_id: location_id,
                    sublocation_id: sublocation_id
                };

                console.log('Sending data to API:', data);

                let path = "/api/inventory/get_damages_history/";
                let that = this;
                
                that.loadingDamagesHistory = true;
                
                let s = function(response) {
                    console.log('Damages History API response:', response.data);
                    that.loadingDamagesHistory = false;
                    
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                        that.damagesHistory = [];
                    } else if (response.data.result) {
                        that.damagesHistory = response.data.result.map(damage => {
                            const sourceLocationName = that.packet.locsubsdict[damage.source_locsub] || `Location ${damage.source_locsub}`;
                            const targetLocationName = that.packet.locsubsdict[damage.target_locsub] || `Location ${damage.target_locsub}`;

                            const sourceDisplay = that.formatLocationDisplay(sourceLocationName);
                            const targetDisplay = that.formatLocationDisplay(targetLocationName);

                            return {
                                id: damage.id,
                                product_name: that.currentDamagesItem.product_name,
                                product_sku: damage.sku || that.currentDamagesItem.product_sku,
                                source_location: sourceLocationName,
                                source_sublocation: that.getSublocationNameFromLocsub(damage.source_locsub),
                                target_location: targetLocationName,
                                target_sublocation: that.getSublocationNameFromLocsub(damage.target_locsub),
                                source_location_display: sourceDisplay,
                                target_location_display: targetDisplay,
                                damage_type: that.formatDamageType(damage.damage_type),
                                damage_grade: that.formatDamageGrade(damage.damage_grade),
                                damage_description: damage.damage_description,
                                original_price: damage.original_price,
                                selling_price: damage.selling_price,
                                moved_by: damage.created_by || 'System',
                                moved_date: damage.created_at,
                                status: damage.status,
                                images: damage.images || []
                            };
                        });
                        
                        console.log('Processed damages history for:', that.currentDamagesItem.product_name, that.damagesHistory);
                    } else {
                        that.damagesHistory = [];
                    }
                }

                let f = function(error) {
                    console.error('Damages History API error:', error);
                    that.loadingDamagesHistory = false;
                    that.$bvToast.toast(
                        "Error fetching damages history.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    that.damagesHistory = [];
                }
                
                Requestor.getSync(path, data, s, f);
            },

            formatLocationDisplay: function(fullLocationName) {
                if (!fullLocationName || fullLocationName === 'undefined') return 'Unknown Location';
                
                if (fullLocationName.includes(' - ')) {
                    return fullLocationName;
                }
                
                return fullLocationName;
            },
            getSublocationNameFromLocsub: function(locsub) {
                if (!locsub || !this.packet.locsubsdict) return 'Unknown';
                
                const fullLocationName = this.packet.locsubsdict[locsub];
                
                if (fullLocationName) {
                    const parts = fullLocationName.split(' - ');
                    return parts.length > 1 ? parts[1] : parts[0];
                }
                
                return `Sublocation ${locsub.split('|')[1]}`;
            },

            formatDamageType: function(type) {
                const typeMap = {
                    'customer_return': 'Customer Return',
                    'shipping': 'Shipping Damage',
                    'warehouse_handling': 'Warehouse Handling',
                    'vendor_fault': 'Vendor Fault',
                    'quality_control': 'Quality Control',
                    'other': 'Other'
                };
                return typeMap[type] || type;
            },

            formatDamageGrade: function(grade) {
                const gradeMap = {
                    'light': 'Light Damage',
                    'moderate': 'Moderate Damage',
                    'heavy': 'Heavy Damage'
                };
                return gradeMap[grade] || grade;
            },

            // Update the damages count to show from damaged_items table
            updateDamagesCount: function() {
                let path = "/api/inventory/get_damages_count/";
                let that = this;
                
                this.items.forEach(item => {
                    let data = {
                        product_sku: item.product_sku,
                        product_id: item.product_id
                    };
                    
                    let s = function(response) {
                        if (response.data.result !== undefined) {
                            // Find the item and update its damages count
                            let targetItem = that.items.find(i => i.product_sku === item.product_sku);
                            if (targetItem) {
                                targetItem.damages = response.data.result;
                                targetItem.damages_fake = response.data.result;
                            }
                        }
                    }
                    
                    Requestor.getSync(path, data, s);
                });
            },
            show_add_to_damages: function(item) {
                this.selectedItem = item;
                
                // Pre-populate form with item data
                this.newDamageItem.original_price = item.selling_price || null;
                this.newDamageItem.selling_price = Math.round((item.selling_price || 0) * 0.5 * 100) / 100;
                this.newDamageItem.quantity = 1;
                this.newDamageItem.damage_type = 'warehouse_handling';
                this.newDamageItem.damage_grade = 'moderate';
                
                // Set current location as default for source
                this.newDamageItem.source_locsub = item.locsub;
                
                // Initialize available locations
                this.initializeAvailableLocations();
                
                this.$bvModal.show('modal-add-to-damages');
            },

            initializeAvailableLocations: function() {
                // Create combined location options from packet.locsubs
                this.availableLocations = this.packet.locsubs.map(loc => ({
                    value: loc.value, // This is the "25|161" format
                    text: loc.text    // This is the "Main Warehouse - Shelf A1" format
                }));
                
                // Add a disabled "Select Location" option at the beginning
                this.availableLocations.unshift({
                    value: null,
                    text: "Select Location",
                    disabled: true
                });
            },
            
            onSourceLocationChange: function() {
                console.log('Source location changed to:', this.newDamageItem.source_locsub);
            },
            
            onTargetLocationChange: function() {
                console.log('Target location changed to:', this.newDamageItem.target_locsub);
            },
        
            handleImageUpload: function(event) {
                const files = event.target.files;
                this.imagePreviews = [];
                
                for (let file of files) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.imagePreviews.push(e.target.result);
                    };
                    reader.readAsDataURL(file);
                }
            },
        
            getMaxAvailableQuantity: function() {
                if (!this.selectedItem || !this.newDamageItem.source_locsub) {
                    return this.selectedItem ? this.selectedItem.in_stock : 0;
                }
                
                // If source is the current item's location, use its stock
                if (this.newDamageItem.source_locsub === this.selectedItem.locsub) {
                    return this.selectedItem.in_stock;
                }
                
                return this.selectedItem.in_stock;
            },
            removeImage: function(index) {
                this.imagePreviews.splice(index, 1);
            },
            
            reset_damages_form: function() {
                // Don't reset if currently saving
                if (this.isSavingDamages) {
                    return;
                }
                
                this.newDamageItem = {
                    source_locsub: null,
                    target_locsub: null,
                    damage_type: '',
                    damage_grade: 'moderate',
                    damage_description: '',
                    original_price: null,
                    selling_price: null,
                    quantity: 1
                };
                this.imagePreviews = [];
                // Clear the file input
                if (this.$refs.damageImagesInput) {
                    this.$refs.damageImagesInput.reset();
                }
                this.$bvModal.hide('modal-add-to-damages');
            },
            
            save_damaged_item: function() {
                // Prevent multiple submissions
                if (this.isSavingDamages) {
                    console.log('Already saving, please wait...');
                    this.$bvToast.toast(
                        "Please wait, the previous request is still processing...",
                        {title: "Processing", autoHideDelay: 1500, variant:"info"}
                    );
                    return;
                }
                
                if (!this.validate_damage_form()) {
                    return;
                }
                
                // Set loading flag
                this.isSavingDamages = true;
                
                console.log('Image previews before sending:', this.imagePreviews);
                
                // Use the combined locsub format (like "25|62") instead of separate IDs
                let data = {
                    product_id: this.selectedItem.product_id,
                    product_sku: this.selectedItem.product_sku,
                    
                    // Use the combined locsub format that your backend expects
                    source_locsub: this.newDamageItem.source_locsub,
                    target_locsub: this.newDamageItem.target_locsub,
                    
                    quantity: this.newDamageItem.quantity,
                    damage_type: this.newDamageItem.damage_type,
                    damage_grade: this.newDamageItem.damage_grade,
                    damage_description: this.newDamageItem.damage_description,
                    original_price: this.newDamageItem.original_price,
                    selling_price: this.newDamageItem.selling_price,
                    images: this.imagePreviews,
                    username: this.packet.userDetails.username
                };
                
                console.log('Final payload with images:', data);
                
                let path = "/api/inventory/add_to_damages/";
                let that = this;
                
                let s = function(response) {
                    console.log('Add to damages response:', response.data);
                    // Reset loading flag
                    that.isSavingDamages = false;
                    
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    } else {
                        that.$bvToast.toast(
                            "ITEM ADDED TO AS IS SUCCESSFULLY!",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.reset_damages_form();
                        that.get_inventory_for_location();
                        
                        if (that.selectedItem) {
                            that.loadItemDamagesCount(that.selectedItem, null);
                        }
                    }
                }
                
                let f = function(error) {
                    console.error('Add to damages error:', error);
                    // Reset loading flag
                    that.isSavingDamages = false;
                    that.$bvToast.toast(
                        "Error adding item to as is. Please try again.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                }
                
                Requestor.postAsyncP(path, data, this, s, f);
            },
            
            validate_damage_form: function() {
                // Check if source and target are different
                if (this.newDamageItem.source_locsub === this.newDamageItem.target_locsub) {
                    this.$vex.dialog.alert({
                        message: "Source and target locations cannot be the same. Please select different locations."
                    });
                    return false;
                }
                
                // Check if source is selected
                if (!this.newDamageItem.source_locsub) {
                    this.$vex.dialog.alert({
                        message: "Please select a source location"
                    });
                    return false;
                }
                
                // Check if target is selected
                if (!this.newDamageItem.target_locsub) {
                    this.$vex.dialog.alert({
                        message: "Please select a target location"
                    });
                    return false;
                }
                
                // Check quantity
                if (this.newDamageItem.quantity > this.getMaxAvailableQuantity()) {
                    this.$vex.dialog.alert({
                        message: `Cannot mark more than available stock. Available from source: ${this.getMaxAvailableQuantity()}`
                    });
                    return false;
                }
                
                if (!this.newDamageItem.damage_type) {
                    this.$vex.dialog.alert({
                        message: "Please select a damage type"
                    });
                    return false;
                }
                
                if (!this.newDamageItem.selling_price) {
                    this.$vex.dialog.alert({
                        message: "Please enter a selling price for the damaged item"
                    });
                    return false;
                }
                
                return true;
            },
            handleResize() {
                this.isMobile = window.innerWidth < 768
            },
            show_reserved_invoices: function(item) {
                this.selectedItem = item;

                // Check if we have invoice data
                if (item.invoices && item.invoices.length > 0) {
                    this.reservedInvoices = item.invoices;
                } else {
                    this.reservedInvoices = [];
                }

                this.$bvModal.show('modal-reserved-invoices');
            },
            formatDate: function(dateString) {
                // Handle both ISO format and GMT format
                if (dateString.includes('GMT')) {
                    return moment(dateString).format('DD-MM-YYYY');
                } else {
                    return moment(dateString).format('DD-MM-YYYY');
                }
            },
            allowed: function(row){
                let status = true;
                let that = this;

                function picked_locsub_fn(row){
                    let status = true;
                    if (that.picked_locsub != null){
                        if (row.locsub != that.picked_locsub){
                            status = false
                        }
                        else {
                            status = true
                        }
                    }
                    return status
                }

                function picked_category_fn(row){
                    let status = true;
                    if (that.picked_category != null){
                        if (row.category != that.picked_category){
                            status = false;
                        }
                    }
                    return status
                }

                function hide_zero_in_stock_fn(row){
                    let status = true;
                    if (that.hide_zero_in_stock){
                        if (row.in_stock == 0){
                            status = false;
                        }
                    }
                    return status
                }

                function search_txt_fn(row){
                    let status = true;
                    let product_sku = row.product_sku;
                    let product_name = row.product_name;
                    status = (
                        product_name.toLowerCase().indexOf(that.search_txt.toLowerCase()) > -1
                        ||
                        product_sku.toLowerCase().indexOf(that.search_txt.toLowerCase()) > -1
                    );
                    return status
                }

                if ((picked_locsub_fn(row) == false) ||
                    (picked_category_fn(row) == false) ||
                    (hide_zero_in_stock_fn(row) == false) ||
                    (search_txt_fn(row) == false)){
                    status = false
                }

                return status
            },

            downloadExcel: function() {
                let filtered = this.items.filter(row => this.allowed(row));

                if (filtered.length === 0) {
                    alert("No data to export!");
                    return;
                }

                let ws_data = filtered.map(item => ({
                    Product: item.product_name,
                    SKU: item.product_sku,
                    Category: item.category,
                    Sublocation: this.packet.locsubsdict[item.locsub],
                    Ordered: item.ordered || 0,
                    Scheduled: item.scheduled || 0,
                    InStock: item.in_stock,
                    Reserved: item.reserved,
                    Available: item.available,
                    Received: item.received || 0,
                    Missing: item.missing || 0,
                    Damages: item.damages || 0,
                    Sold: item.sold || 0,
                    SellingPrice: item.selling_price,
                    WholesalePrice: item.wholesale_price,
                    LandedCost: item.landed_cost,
                }));

                function escapeCSV(value) {
                    if (value === null || value === undefined) return "";
                    value = value.toString();
                    if (/[",\n]/.test(value)) {
                        value = '"' + value.replace(/"/g, '""') + '"';
                    }
                    return value;
                }

                let headers = Object.keys(ws_data[0]).map(escapeCSV).join(",");
                let rows = ws_data.map(obj => Object.values(obj).map(escapeCSV).join(",")).join("\n");

                let csvContent = "data:text/csv;charset=utf-8," + headers + "\n" + rows;

                let encodedUri = encodeURI(csvContent);
                let link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", "inventory_export.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            },

            submit_move_wrapper: function(item){
                if (!item.to_locsub){
                   this.$vex.dialog.alert({
                        message: "Please select a target location to move items to."
                    });
                    return
                }

                if (item.locsub == item.to_locsub){
                   this.$vex.dialog.alert({
                        message: "Cannot move inventory into the same location."
                    });
                    return
                }

                item.move_quantity = parseInt(item.move_quantity)
                item.in_stock = parseInt(item.in_stock);

                if (isNaN(item.move_quantity)){
                    this.$vex.dialog.alert({
                        message: "Please enter a valid quantity to move."
                    });
                    return
                }
                else {
                    if (item.move_quantity > item.in_stock){
                       this.$vex.dialog.alert({
                            message: "You cannot move more than available items."
                        });
                        return
                    }
                    else {
                        this.submit_move(item);
                    }
                }
            },

            submit_move: function(item){
                let path = "/api/inventory/move_inventory/"
                let data = {
                    product_id: item.product_id,
                    product_sku: item.product_sku,
                    from_locsub: item.locsub,
                    to_locsub: item.to_locsub,
                    move_quantity: item.move_quantity,
                    username: this.packet.userDetails.username,
                }
                let that = this;
                let s = function(response) {
                    if (response.data.result){
                        that.$bvToast.toast(
                            "MOVE SUCCESSFUL!",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.get_inventory_for_location()

                    }
                    else {
                        that.$bvToast.toast(
                            "ERROR MOVING INVENTORY",
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                        console.log(response.data.error);
                    }
                    that.items_being_loaded = false;
                }

                let f = function(error) {
                    that.$bvToast.toast(
                        "Error moving inventory between locations.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    that.items_being_loaded = false;
                }

                this.items_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },

            cancel_move: function(item){
                item.move_items = false;
            },

            move_items: function(item){
                item.move_items = true;
            },

            get_inventory_for_location: function(){
                let path = "/api/inventory/get_inventory_for_location/"
                let that = this;
                let data = {location_id: this.location_id}
                let s = function(response) {
                    if (response.data.result){
                        that.items = response.data.result.map(item => ({
                            ...item,
                            received: item.received || 0,
                            missing: item.missing || 0,
                            damages: 0,
                            sold: item.sold || 0,
                            actual_damages_count: 0,
                            damages_count_loaded: false, // Initialize as not loaded
                            // Remove all edit fields for damages
                            edit_received: false,
                            edit_missing: false,
                            edit_sold: false,
                            received_fake: item.received || 0,
                            missing_fake: item.missing || 0,
                            sold_fake: item.sold || 0
                        }));
                        
                        // DON'T fetch damages counts automatically anymore
                        // that.$nextTick(() => {
                        //     that.fetchDamagesCountsForItems();
                        // });
                    }
                    else {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    that.items_being_loaded = false;
                }

                let f = function(error) {
                    that.$bvToast.toast(
                        "Error retrieving inventory for location.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    that.items_being_loaded = false;
                }

                this.items_being_loaded = true;
                this.items = [];
                this.picked_locsub = null;
                Requestor.getSync(path, data, s, f);
            },

            sortby: function(key){
                if (this.sort_asc){
                    this.items = Requestor.sort(this.items, key)
                }
                else {
                    this.items = Requestor.sort_reverse(this.items, key)
                }
                this.sort_asc = !this.sort_asc;
            },

            total: function(key, qty){
                let that = this;
                let out = 0.0;
                if (qty){
                    out = this.items.filter(function(d){return that.allowed(d) == true }).map(function(d){return d[key] * d.in_stock }).reduce((a, b) => a + b, 0)
                }
                else {
                    out = this.items.filter(function(d){return that.allowed(d) == true }).map(function(d){return d[key] }).reduce((a, b) => a + b, 0)
                }
                return out
            },

            numberWithCommas: function(x) {
                return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            },

            fetch_prereqs: function(){
                // Implementation if needed
            },

            edit_in_stock: function(item){
                item.edit_in_stock = true;
                return
            },

            cancel_save_in_stock: function(item){
                item.edit_in_stock = false;
                return
            },

            save_in_stock: function(item){
                let product_sku=item.product_sku;
                let splitted = item.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);
                let nitems = parseInt(item.in_stock_fake);
                if (isNaN(nitems)){
                    this.$vex.dialog.alert({
                        message: "Please specify a valid quantity for the item"
                    });
                    return
                }
                if (isNaN(location_id) || isNaN(sublocation_id)){
                    this.$vex.dialog.alert({
                        message: "Invalid location for the item. Please refresh the page."
                    });
                    return
                }
                if (!product_sku){
                    this.$vex.dialog.alert({
                        message: "Invalid product. Please refresh the page."
                    });
                    return
                }
                let data = {
                    product_sku: product_sku,
                    location_id: location_id,
                    sublocation_id: sublocation_id,
                    nitems: nitems
                };

                let path = "/api/inventory/update_inventory/"
                let that = this;
                let s = function(response) {
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "UPDATED PRODUCT INVENTORY.",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item.edit_in_stock=false;
                        item.in_stock = parseInt(item.in_stock_fake);
                    }
                }

                let f = function(error) {
                    that.$bvToast.toast(
                        "Error updating inventory. Please try again later.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    item.edit_in_stock=false;

                }
                Requestor.postAsyncP(path, data, this, s, f);
                return
            },

            // NEW METHODS FOR RECEIVED, MISSING, DAMAGES, SOLD
            edit_received: function(item) {
                item.edit_received = true;
                item.received_fake = item.received || 0;
            },

            cancel_save_received: function(item) {
                item.edit_received = false;
            },

            save_received: function(item) {
                this.updateInventoryField(item, 'received', item.received_fake, 'RECEIVED');
            },

            edit_missing: function(item) {
                item.edit_missing = true;
                item.missing_fake = item.missing || 0;
            },

            cancel_save_missing: function(item) {
                item.edit_missing = false;
            },

            save_missing: function(item) {
                this.updateInventoryField(item, 'missing', item.missing_fake, 'MISSING');
            },

            edit_damages: function(item) {
                item.edit_damages = true;
                item.damages_fake = item.damages || 0;
            },

            cancel_save_damages: function(item) {
                item.edit_damages = false;
            },

            save_damages: function(item) {
                this.updateInventoryField(item, 'damages', item.damages_fake, 'DAMAGES');
            },

            edit_sold: function(item) {
                item.edit_sold = true;
                item.sold_fake = item.sold || 0;
            },

            cancel_save_sold: function(item) {
                item.edit_sold = false;
            },

            save_sold: function(item) {
                this.updateInventoryField(item, 'sold', item.sold_fake, 'SOLD');
            },

            updateInventoryField: function(item, field, value, fieldName) {
                console.log('Updating field:', field, 'with value:', value, 'for item:', item);

                let splitted = item.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);

                console.log('Parsed location data:', {
                    location_id: location_id,
                    sublocation_id: sublocation_id,
                    product_sku: item.product_sku
                });

                if (isNaN(value)) {
                    this.$vex.dialog.alert({
                        message: `Please specify a valid quantity for ${fieldName}`
                    });
                    return;
                }

                let data = {
                    product_sku: item.product_sku,
                    location_id: location_id,
                    sublocation_id: sublocation_id,
                    field: field,
                    value: parseInt(value),
                    username: this.packet.userDetails.username
                };

                console.log('Sending data to backend:', data);

                let path = "/api/inventory/update_inventory_field/";
                let that = this;
                let s = function(response) {
                    console.log('Backend response:', response.data);
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    } else {
                        that.$bvToast.toast(
                            `UPDATED ${fieldName} QUANTITY.`,
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item[field] = parseInt(value);
                        item[`edit_${field}`] = false;
                    }
                }

                let f = function(error) {
                    console.error('Request failed:', error);
                    that.$bvToast.toast(
                        `Error updating ${fieldName.toLowerCase()} quantity. Please try again later.`,
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    item[`edit_${field}`] = false;
                }

                Requestor.postAsyncP(path, data, this, s, f);
            },
            // HISTORY METHODS
            show_received_history: function(item) {
                this.selectedItem = item;
                this.fetchHistory('received');
                this.$bvModal.show('modal-received-history');
            },

            show_missing_history: function(item) {
                this.selectedItem = item;
                this.fetchHistory('missing');
                this.$bvModal.show('modal-missing-history');
            },

            show_sold_history: function(item) {
                this.selectedItem = item;
                this.fetchHistory('sold');
                this.$bvModal.show('modal-sold-history');
            },

            fetchHistory: function(field) {
                if (!this.selectedItem) return;

                let splitted = this.selectedItem.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);

                let data = {
                    product_sku: this.selectedItem.product_sku,
                    location_id: location_id,
                    sublocation_id: sublocation_id,
                    field: field
                };

                let path = "/api/inventory/get_inventory_history/";
                let that = this;
                let s = function(response) {
                    console.log('History API response:', response.data);
                    if (response.data.result) {
                        // Enhance the history data with location names
                        let enhancedHistory = response.data.result.map(item => {
                            // Fix for optional chaining - use traditional approach
                            let locationName = 'Unknown';
                            if (that.packet.locations) {
                                let location = that.packet.locations.find(function(loc) {
                                    return loc.value === location_id;
                                });
                                locationName = location ? location.text : 'Unknown';
                            }

                            let sublocationKey = location_id + '|' + sublocation_id;
                            let sublocationName = that.packet.locsubsdict[sublocationKey] || 'Unknown';

                            return {
                                ...item,
                                location_name: locationName,
                                sublocation_name: sublocationName
                            };
                        });

                        if (field === 'received') {
                            that.receivedHistory = enhancedHistory;
                        } else if (field === 'missing') {
                            that.missingHistory = enhancedHistory;
                        } else if (field === 'damages') {
                            that.damagesHistory = enhancedHistory;
                        } else if (field === 'sold') {
                            that.soldHistory = enhancedHistory;
                        }
                    } else {
                        that.$bvToast.toast(
                            response.data.error || "Error fetching history",
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                }

                let f = function(error) {
                    console.error('History API error:', error);
                    that.$bvToast.toast(
                        "Error fetching history data.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                }
                Requestor.getSync(path, data, s, f);
            },
            saveReceivedHistory: function() {
                this.saveHistory('received', this.newReceivedQty);
            },

            saveMissingHistory: function() {
                this.saveHistory('missing', this.newMissingQty);
            },

            saveDamagesHistory: function() {
                this.saveHistory('damages', this.newDamagesQty);
            },

            saveSoldHistory: function() {
                this.saveHistory('sold', this.newSoldQty);
            },

            saveHistory: function(field, quantity) {
                if (!this.selectedItem || !quantity || quantity <= 0) {
                    this.$vex.dialog.alert({
                        message: "Please enter a valid quantity"
                    });
                    return;
                }

                let splitted = this.selectedItem.locsub.split("|");
                let location_id = parseInt(splitted[0]);
                let sublocation_id = parseInt(splitted[1]);

                let data = {
                    product_sku: this.selectedItem.product_sku,
                    location_id: location_id,
                    sublocation_id: sublocation_id,
                    field: field,
                    quantity: parseInt(quantity),
                    username: this.packet.userDetails.username,
                    source: 'Manual Entry' // You can modify this based on your needs
                };

                let path = "/api/inventory/add_inventory_history/";
                let that = this;
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    } else {
                        that.$bvToast.toast(
                            `ADDED ${field.toUpperCase()} HISTORY.`,
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.newReceivedQty = 0;
                        that.newMissingQty = 0;
                        that.newDamagesQty = 0;
                        that.newSoldQty = 0;
                        that.fetchHistory(field);
                        that.get_inventory_for_location(); // Refresh main data
                    }
                }

                let f = function(error) {
                    that.$bvToast.toast(
                        `Error adding ${field} history.`,
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                }
                Requestor.postAsyncP(path, data, this, s, f);
            },

            deleteHistoryItem: function(field, historyId) {
                let data = {
                    history_id: historyId
                };

                let path = "/api/inventory/delete_inventory_history/";
                let that = this;
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    } else {
                        that.$bvToast.toast(
                            "DELETED HISTORY ITEM.",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.fetchHistory(field);
                        that.get_inventory_for_location(); // Refresh main data
                    }
                }

                let f = function(error) {
                    that.$bvToast.toast(
                        "Error deleting history item.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                }
                Requestor.postAsyncP(path, data, this, s, f);
            },

        },
        created: function(){
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            let that = this;
            if (this.packet.userDetails.properties.location_id){
                let location_id = this.packet.userDetails.properties.location_id;
                this.locations= this.packet.locations.filter(function(d){
                    return (d.value == location_id || d.is_main_warehouse == true)
                });
            }
            else {
                this.locations= this.packet.locations;
            }

            this.locations = this.locations.filter(function(d){return d.text != 'All'});
            this.locations = this.locations.sort(function(a,b){return a.is_main_warehouse - b.is_main_warehouse })
            this.$nextTick(() => {
                if (this.items.length > 0) {
                    this.updateDamagesCount();
                }
            });
        }
    }
</script>

<style scoped>
/* Damage Images Styling */
.damage-images-thumbnails {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}

.damage-thumbnail {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s;
}

.damage-thumbnail:hover {
    border-color: #007bff;
    transform: scale(1.1);
}

.simple-image-thumbnails {
    display: flex;
    gap: 3px;
    margin-bottom: 5px;
}

.simple-thumbnail {
    width: 35px;
    height: 35px;
    object-fit: cover;
    border-radius: 3px;
    cursor: pointer;
    border: 1px solid #dee2e6;
}

.simple-thumbnail:hover {
    border-color: #007bff;
}

/* Gallery Styling */
.main-image-container {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
}

.main-damage-image {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
}

.image-counter {
    font-weight: 600;
    color: #495057;
}

.thumbnail-strip {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 8px;
}

.thumbnail-container {
    cursor: pointer;
    border: 3px solid transparent;
    border-radius: 4px;
    transition: all 0.2s;
}

.thumbnail-container.active {
    border-color: #007bff;
}

.thumbnail-container:hover {
    border-color: #0056b3;
}

.thumbnail-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 2px;
}

/* Modal Table Styling */
/* Invoice link styling */
.invoice-link {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s;
}

.invoice-link:hover {
    color: #0056b3;
    background-color: #f8f9fa;
    text-decoration: none;
}

/* Highlight reserved items */
.text-warning {
    font-weight: bold;
}

/* Make the reserved cell more prominent when there are reservations */
.clickable-cell.text-warning {
    background-color: #fff3cd;
    border-radius: 4px;
    padding: 2px 8px;
    display: inline-block;
}

.history-table-card {
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.compact-table {
    font-size: 0.8rem;
}

.compact-table th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #2c3e50;
    border-bottom: 2px solid #dee2e6;
    padding: 8px 12px;
}

.compact-table td {
    padding: 6px 12px;
    vertical-align: middle;
    border-color: #e9ecef;
}

/* Table responsive wrapper */
.table-responsive {
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

/* Quantity badges */
.compact-table .badge {
    font-size: 0.75rem;
    padding: 4px 8px;
}

/* Action buttons */
.action-btn {
    padding: 2px 6px;
    border: none;
}

/* Timestamp and user cells */
.timestamp-cell, .user-cell {
    white-space: nowrap;
    font-size: 0.75rem;
    color: #6c757d;
}

/* Source badges */
.source-badge {
    font-size: 0.7rem;
    padding: 3px 6px;
}

/* Empty state */
.compact-table .table-empty {
    padding: 20px;
    text-align: center;
    color: #6c757d;
}

.clickable-cell {
    cursor: pointer;
    padding: 2px 5px;
    border-radius: 3px;
    transition: background-color 0.2s;
}

.clickable-cell:hover {
    background-color: #f8f9fa;
}

.ipt_tdhr {
    padding: 8px;
    border: 1px solid #dee2e6;
}

/* Add some spacing for the edit inputs */
.b-input {
    margin-right: 5px;
}

.b-badge {
    margin-right: 3px;
    cursor: pointer;
}

/* Loading indicator for damages count */
.damages-loading {
    font-style: italic;
    color: #6c757d;
    font-size: 0.9em;
}
</style>


// WEBPACK FOOTER //
// src/components/Inventory/DamagesManagement.vue